import { useContext, useEffect, useState } from "react";
import {
  Button,
  Col,
  ConfigProvider,
  Dropdown,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import { Provider } from "../redux/api/provider/types";
import {
  Header,
  Pagination,
  StatusIndicator,
  Table,
} from "@cloudscape-design/components";
import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  CheckCircleOutlined,
  DownOutlined,
  EditOutlined,
  FileTextOutlined,
  MailOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useProviderFilter from "../table-filters/provider-filters";
import { useLocalStorage } from "../common/localStorage";
import {
  PROVIDER_CONTENT_DISPLAY_OPTIONS,
  PROVIDER_TABLE_DEFAULT_PREFERENCES,
  TablePreferences,
} from "./table-config";
import {
  useActiveProviderMutation,
  useInActiveAllProviderMutation,
  useInviteToMobileAppProviderMutation,
} from "../redux/api/provider/provider";
import { SpinnerContext } from "../context/spinner/spinner";
import { ToastContext } from "../context/toast/incredable-toast";
import { isEmpty } from "lodash";
import IncredableButton from "../components/button";
import { useSelector } from "react-redux";
import { ApplicationState } from "../redux/store";
import { useGetOrganizationDetailsQuery } from "../redux/api/organization/organization";
import { useGetAllRequestTemplatesQuery } from "../redux/api/requesttemplate/requesttemplate";
import { useSendDocumentRequestTemplateMutation } from "../redux/api/documentrequest/documentrequest";

export default function ProviderListV3() {
  const [selectedRows, setSelectedRows] = useState<Provider[]>();
  const [spin, setSpin] = useState<boolean>(false);
  const { data: organization } = useGetOrganizationDetailsQuery();

  const {
    TableFilters,
    filteredProviders = [],
    providerTotalRecords,
    UseQueryHookResult: {
      reload,
      isFetching,
      pageNumber,
      setPageNumber,
      pageSize,
      statusQueryFilterValue,
      setPageSize,
      sortingDetails,
      setSortingDetails,
    },
  } = useProviderFilter();
  const [isRequestTemplateModalOpen, setIsRequestTemplateModalOpen] =
    useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<number[]>([]);
  const [sendDocumentRequestTemplate] =
    useSendDocumentRequestTemplateMutation();
  const [activeProvider] = useActiveProviderMutation();
  const [deActiveAllProvider] = useInActiveAllProviderMutation();
  const [inviteToMobileApp] = useInviteToMobileAppProviderMutation();
  const { data: requestTemplates } = useGetAllRequestTemplatesQuery();
  const spinnerContext = useContext(SpinnerContext);
  const toastContext = useContext(ToastContext);
  const hasPermission = useSelector(
    (state: ApplicationState) =>
      state.ui.is_owner ||
      (state.ui.permissions?.includes("provider.edit") ?? false)
  );
  const hasDeletePermission = useSelector(
    (state: ApplicationState) =>
      state.ui.is_owner ||
      (state.ui.permissions?.includes("provider.delete") ?? false)
  );

  const [tablePreferences, setTablePreferences] = useLocalStorage(
    "RD-Provider-Table-Preferences",
    PROVIDER_TABLE_DEFAULT_PREFERENCES
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (tablePreferences?.pageSize) setPageSize(tablePreferences?.pageSize);
  }, [setPageSize, tablePreferences?.pageSize]);

  const { items, collectionProps, paginationProps } = useCollection(
    filteredProviders,
    {
      pagination: { pageSize: pageSize },
    }
  );

  return (
    <div style={{ padding: "32px 32px 0 32px" }}>
      <Spin spinning={spin} fullscreen />
      <Table
        loading={isFetching}
        onSelectionChange={({ detail }) => {
          setSelectedRows([...detail.selectedItems]);
        }}
        onRowClick={(onRowClickDetails) => {
          const selectedItem = onRowClickDetails?.detail?.item;
          if (selectedItem) {
            const temp = selectedRows ? [...selectedRows] : [];
            const selectedItemIndex = temp.findIndex(
              (record) => record?.id + "" === "" + selectedItem?.id
            );
            if (selectedItemIndex !== -1) {
              temp?.splice(selectedItemIndex, 1);
              setSelectedRows(temp);
            } else {
              setSelectedRows([...temp, selectedItem]);
            }
          }
        }}
        selectedItems={selectedRows || []}
        sortingColumn={{ sortingField: sortingDetails?.sortingField }}
        sortingDescending={sortingDetails?.isDescending}
        onSortingChange={({ detail }) => {
          setSortingDetails({
            sortingField: detail?.sortingColumn?.sortingField,
            isDescending: detail?.isDescending,
          });
        }}
        columnDefinitions={[
          {
            id: "id",
            header: "Id",
            cell: (item) => item?.id,
            sortingField: "id",
          },
          {
            id: "fullname",
            header: "Full Name",
            cell: (item) => (
              <Typography.Link
                onClick={(e) => {
                  e.preventDefault();
                  setSpin(true);
                  setTimeout(() => navigate(`${item.id}`), 300);
                }}
              >
                {`${item.lastName}, ${item.firstName}`}
              </Typography.Link>
            ),
            sortingField: "lastName",
            isRowHeader: true,
          },
          {
            id: "lastName",
            header: "Last Name",
            cell: (item) => item?.lastName,
            sortingField: "lastName",
          },
          {
            id: "firstName",
            header: "First Name",
            cell: (item) => item?.firstName,
            sortingField: "firstName",
          },
          {
            id: "npiNumber",
            header: "NPI Number",
            cell: (item) => item?.npiNumber,
            sortingField: "npiNumber",
          },
          {
            id: "email",
            header: "Email Address",
            cell: (item) => item?.email,
            sortingField: "email",
          },
          {
            id: "licenseType",
            header: "License Type",
            cell: (item) => item?.licenseType?.name,
            sortingField: "licenseType.name",
          },
          {
            id: "facility",
            header: "Facility",
            cell: (item) => item?.facility?.name,
            sortingField: "facility.name",
          },
          {
            id: "facilityGroup",
            header: "Facility Group",
            cell: (item) => item?.facilityGroup?.name,
            sortingField: "facilityGroup.name",
          },
          {
            id: "employmentType",
            header: "Employment Type",
            cell: (item) => item?.providerStatus?.status ?? "-",
            sortingField: "providerStatus.status",
          },
          {
            id: "status",
            header: <div>Status</div>,
            cell: (item) => (
              <StatusIndicator
                type={item?.isActive === true ? "success" : "stopped"}
                colorOverride={item?.isActive === true ? "green" : "red"}
              >
                {item?.isActive === true ? "Active" : "Inactive"}
              </StatusIndicator>
            ),
          },
        ]}
        items={items}
        pagination={
          <Pagination
            {...paginationProps}
            currentPageIndex={pageNumber + 1}
            onChange={({ detail }) => {
              setPageNumber(detail.currentPageIndex - 1);
            }}
            pagesCount={
              !!tablePreferences?.pageSize && tablePreferences?.pageSize !== 0
                ? Math.ceil(providerTotalRecords / tablePreferences?.pageSize)
                : 1
            }
          />
        }
        preferences={
          <TablePreferences
            preferences={tablePreferences}
            setPreferences={(preferences) => {
              if (preferences.pageSize !== tablePreferences.pageSize) {
                preferences.pageSize && setPageSize(preferences.pageSize);
              }
              setTablePreferences(preferences);
            }}
            contentDisplayOptions={PROVIDER_CONTENT_DISPLAY_OPTIONS}
          />
        }
        loadingText="Loading resources"
        selectionType="multi"
        trackBy="id"
        filter={TableFilters}
        header={
          <div style={{ marginTop: "8px", marginBottom: "12px" }}>
            <Header
              actions={
                <ConfigProvider
                  theme={{
                    token: {
                      colorPrimary: "#2B6BE6",
                    },
                  }}
                >
                  <Space size="middle">
                    <Button
                      onClick={() => {
                        !!reload && reload();
                      }}
                    >
                      <ReloadOutlined />
                    </Button>
                    <Button
                      onClick={() => {
                        navigate(`/safe?providerId=${selectedRows?.at(0)?.id}`);
                      }}
                      disabled={!selectedRows || selectedRows?.length !== 1}
                      iconPosition={"end"}
                    >
                      Go To Safe
                    </Button>
                    <Dropdown
                      menu={{
                        items: [
                          {
                            label: hasPermission ? "Edit" : "View",
                            key: "edit",
                            disabled: selectedRows?.length !== 1,
                            icon: <EditOutlined />,
                            onClick: () => {
                              setSpin(true);
                              setTimeout(
                                () => navigate(`${selectedRows?.at(0)?.id}`),
                                500
                              );
                            },
                          },
                          ...(!!organization?.settings?.enableMobileApp
                            ? [
                                {
                                  label: "Invite to Mobile App",
                                  key: "invite",
                                  disabled:
                                    (!selectedRows ||
                                      selectedRows.length === 0) &&
                                    hasPermission,
                                  icon: <MailOutlined />,
                                  onClick: async () => {
                                    if (selectedRows) {
                                      const data = selectedRows?.map(
                                        (row) => row.id
                                      );
                                      try {
                                        await inviteToMobileApp({
                                          data,
                                        }).unwrap();
                                        toastContext?.openSuccessNotification({
                                          message: `Invitation send successfully`,
                                          placement: "topRight",
                                        });
                                      } catch (error: any) {
                                        toastContext?.openErrorNotification({
                                          message:
                                            error?.data?.errorMessage ||
                                            `Failed to send inviation`,
                                          placement: "topRight",
                                        });
                                      }
                                      spinnerContext?.hidespinner();
                                    }
                                    setSelectedRows(undefined);
                                  },
                                },
                              ]
                            : []),
                          {
                            label: "Request Template",
                            key: "requestTemplate",
                            disabled:
                              (!selectedRows || selectedRows.length === 0) &&
                              hasPermission,
                            icon: <FileTextOutlined />,
                            onClick: () => {
                              setIsRequestTemplateModalOpen(true);
                            },
                          },
                          {
                            label:
                              statusQueryFilterValue === "inactive"
                                ? "Active"
                                : "InActive",
                            key:
                              statusQueryFilterValue === "inactive"
                                ? "active"
                                : "inactive",
                            icon:
                              statusQueryFilterValue === "inactive" ? (
                                <CheckCircleOutlined />
                              ) : (
                                <MinusCircleOutlined />
                              ),
                            danger:
                              statusQueryFilterValue === "inactive"
                                ? false
                                : true,
                            disabled:
                              !hasDeletePermission &&
                              statusQueryFilterValue === "inactive"
                                ? selectedRows?.length !== 1
                                : isEmpty(selectedRows)
                                ? true
                                : false,
                          },
                        ],
                        onClick: async (e) => {
                          if (e?.key === "inactive") {
                            spinnerContext?.hidespinner();
                            if (selectedRows) {
                              const data = selectedRows?.map((row) =>
                                Number(row.id)
                              );
                              try {
                                await deActiveAllProvider({ data }).unwrap();
                                toastContext?.openSuccessNotification({
                                  message: `Provider Inactive successfully`,
                                  placement: "topRight",
                                });
                              } catch (error: any) {
                                toastContext?.openErrorNotification({
                                  message:
                                    error?.data?.errorMessage ||
                                    `Failed to inactivate provider`,
                                  placement: "topRight",
                                });
                              }
                              spinnerContext?.hidespinner();
                            }
                            setSelectedRows(undefined);
                          }
                          if (e?.key === "active") {
                            spinnerContext?.showSpinner();
                            try {
                              await activeProvider({
                                providerId: String(selectedRows?.at(0)?.id),
                              }).unwrap();
                              toastContext?.openSuccessNotification({
                                message: `Provider Active successfully`,
                                placement: "topRight",
                              });
                            } catch (error: any) {
                              toastContext?.openErrorNotification({
                                message:
                                  error?.data?.errorMessage ||
                                  `Failed to activate provider`,
                                placement: "topRight",
                              });
                            }
                            spinnerContext?.hidespinner();
                            setSelectedRows(undefined);
                          }
                        },
                      }}
                    >
                      <Button>
                        Actions
                        <DownOutlined />
                      </Button>
                    </Dropdown>
                    <IncredableButton
                      onClick={() => navigate("add")}
                      type="primary"
                      icon={<PlusOutlined />}
                      style={{ backgroundColor: "#598BEB" }}
                    >
                      Add Provider
                    </IncredableButton>
                  </Space>
                </ConfigProvider>
              }
            >
              <Typography.Title level={3} style={{ marginTop: "0" }}>
                Providers{" "}
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: "16px" }}
                >{`(${providerTotalRecords})`}</Typography.Text>
              </Typography.Title>
            </Header>
          </div>
        }
        {...collectionProps}
        columnDisplay={tablePreferences?.contentDisplay}
        wrapLines={tablePreferences?.wrapLines}
        stripedRows={tablePreferences?.stripedRows}
        contentDensity={tablePreferences?.contentDensity}
        stickyColumns={tablePreferences?.stickyColumns}
      />
      <Modal
        title="Select Request Template"
        open={isRequestTemplateModalOpen}
        onCancel={() => {
          setSelectedRows([]);
          setSelectedTemplates([]);
          setIsRequestTemplateModalOpen(false);
        }}
        onOk={async () => {
          if (selectedRows && selectedTemplates.length) {
            const providerIds = selectedRows.map((row) => String(row.id));
            const templateIds = selectedTemplates.map((id) => String(id));
            try {
              await sendDocumentRequestTemplate({
                providerIds,
                templateIds,
              }).unwrap();
              toastContext?.openSuccessNotification({
                message: `Requested Template successfully`,
                placement: "topRight",
              });
            } catch (error: any) {
              toastContext?.openErrorNotification({
                message:
                  error?.data?.errorMessage || `Failed to request template`,
                placement: "topRight",
              });
            } finally {
              spinnerContext?.hidespinner();
              setSelectedRows([]);
              setIsRequestTemplateModalOpen(false);
              setSelectedTemplates([]);
            }
          }
        }}
        okText="Save"
        cancelText="Cancel"
        width={600}
        okButtonProps={{
          disabled: selectedTemplates.length < 1,
        }}
      >
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={24}>
            <Select
              mode="multiple"
              value={selectedTemplates}
              onChange={(value: number[]) => setSelectedTemplates(value)}
              allowClear
              showSearch
              style={{ width: "100%", margin: "10px 0 20px 0" }}
              placeholder="Select templates"
              options={requestTemplates?.map((template) => {
                return {
                  label: template?.name,
                  value: template?.id,
                };
              })}
            />
          </Col>
        </Row>
      </Modal>
    </div>
  );
}
